import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { Question } from '../../interfaces/career-setup/question.interface';
import { PageResponse } from '../../interfaces/page-response.interface';
import { QuestionService } from './question.service';

@Injectable({
  providedIn: 'root'
})
export class QuestionMockService extends QuestionService {
  private mockData: Question[] = [
    // Frontend Aptitude Questions
    { uniqueId: 'qst-apt-1', question: 'What will be the output of `console.log(typeof null)`?', questionType: 'MULTIPLE_CHOICE', instruction: 'Choose the correct data type returned by JS.', assessmentUniqueId: 'ass-fe-apt', maxMins: 2, sequenceNo: 1, active: true },
    { uniqueId: 'qst-apt-2', question: 'Which array method allows you to transform elements without mutating the original array?', questionType: 'MULTIPLE_CHOICE', instruction: 'Identify the immutable transformation method.', assessmentUniqueId: 'ass-fe-apt', maxMins: 2, sequenceNo: 2, active: true },
    { uniqueId: 'qst-apt-3', question: 'Explain the difference between `==` and `===` operators.', questionType: 'ESSAY', instruction: 'Write a brief explanation detailing type coercion.', assessmentUniqueId: 'ass-fe-apt', maxMins: 5, sequenceNo: 3, active: true },
    { uniqueId: 'qst-apt-4', question: 'What is the purpose of the Event Loop in JavaScript?', questionType: 'MULTIPLE_CHOICE', instruction: 'Select the description that best describes asynchronous behavior.', assessmentUniqueId: 'ass-fe-apt', maxMins: 3, sequenceNo: 4, active: true },
    { uniqueId: 'qst-apt-5', question: 'How does CSS Grid differ from Flexbox?', questionType: 'ESSAY', instruction: 'Discuss 1D vs 2D layout constraints.', assessmentUniqueId: 'ass-fe-apt', maxMins: 4, sequenceNo: 5, active: true },
    { uniqueId: 'qst-apt-6', question: 'Which keyword is used to handle exceptions in modern async/await?', questionType: 'MULTIPLE_CHOICE', instruction: 'Identify the standard error handling block.', assessmentUniqueId: 'ass-fe-apt', maxMins: 1, sequenceNo: 6, active: true },
    { uniqueId: 'qst-apt-7', question: 'Describe what a "Closure" is in your own words.', questionType: 'ESSAY', instruction: 'Explain scope retention with a brief example.', assessmentUniqueId: 'ass-fe-apt', maxMins: 5, sequenceNo: 7, active: true },
    { uniqueId: 'qst-apt-8', question: 'What CSS property ensures an element is hidden but still takes up space?', questionType: 'MULTIPLE_CHOICE', instruction: 'Choose between display, visibility, and opacity.', assessmentUniqueId: 'ass-fe-apt', maxMins: 2, sequenceNo: 8, active: true },
    { uniqueId: 'qst-apt-9', question: 'What is the Virtual DOM in React/Angular contexts?', questionType: 'MULTIPLE_CHOICE', instruction: 'Select the core performance reason for its use.', assessmentUniqueId: 'ass-fe-apt', maxMins: 3, sequenceNo: 9, active: true },
    { uniqueId: 'qst-apt-10', question: 'Explain the concept of "Debouncing".', questionType: 'ESSAY', instruction: 'Provide a real-world frontend use case for debouncing.', assessmentUniqueId: 'ass-fe-apt', maxMins: 5, sequenceNo: 10, active: true },
    
    // Backend Aptitude Questions
    { uniqueId: 'qst-be-1', question: 'In REST API design, what HTTP status code indicates a resource was successfully created?', questionType: 'MULTIPLE_CHOICE', instruction: 'Select the standard REST status code.', assessmentUniqueId: 'ass-be-apt', maxMins: 2, sequenceNo: 1, active: true },
    { uniqueId: 'qst-be-2', question: 'Which of the following database indexes provides the fastest exact-match lookup?', questionType: 'MULTIPLE_CHOICE', instruction: 'Select the correct terminology.', assessmentUniqueId: 'ass-be-apt', maxMins: 2, sequenceNo: 2, active: true },
  ];

  constructor() {
    super();
  }

  override findAll(params: any): Observable<PageResponse<Question>> {
    let filtered = [...this.mockData];
    
    if (params.assessmentUniqueId) {
      filtered = filtered.filter(q => q.assessmentUniqueId === params.assessmentUniqueId);
    }
    
    if (params.searchTerm) {
      const term = params.searchTerm.toLowerCase();
      filtered = filtered.filter(q => q.question.toLowerCase().includes(term));
    }

    return of({
      content: filtered,
      page: {
        number: params.page || 0,
        size: params.size || 25,
        totalElements: filtered.length,
        totalPages: 1
      }
    });
  }

  override find(id: string): Observable<Question> {
    const item = this.mockData.find(i => i.uniqueId === id);
    return of(item as Question);
  }

  override create(data: Partial<Question>): Observable<Question> {
    const newItem = { ...data, uniqueId: 'mock-qst-' + Date.now() } as Question;
    this.mockData.push(newItem);
    return of(newItem);
  }

  override update(id: string, data: Partial<Question>): Observable<Question> {
    const index = this.mockData.findIndex(i => i.uniqueId === id);
    if (index > -1) {
      this.mockData[index] = { ...this.mockData[index], ...data };
      return of(this.mockData[index]);
    }
    return of(data as Question);
  }

  override updateStatus(id: string, isActive: boolean): Observable<Question> {
    const index = this.mockData.findIndex(i => i.uniqueId === id);
    if (index > -1) {
      this.mockData[index] = { ...this.mockData[index], active: isActive };
      return of(this.mockData[index]);
    }
    return of({ active: isActive } as Question);
  }

  override delete(id: string): Observable<void> {
    this.mockData = this.mockData.filter(i => i.uniqueId !== id);
    return of(undefined);
  }
}
